/**
 * Error Reporting Service
 * Reports frontend errors to the backend monitoring system.
 * Works alongside Sentry for comprehensive error tracking.
 */

import logger from '../utils/logger';
import environmentConfig from '../config/environment';

// Debounce errors to avoid flooding the backend
const errorDebounceMap = new Map();
const DEBOUNCE_MS = 5000; // 5 seconds

/**
 * Check if we should report this error (debounce duplicate errors)
 */
const shouldReportError = (errorKey) => {
  const lastReported = errorDebounceMap.get(errorKey);
  const now = Date.now();

  if (lastReported && now - lastReported < DEBOUNCE_MS) {
    return false;
  }

  errorDebounceMap.set(errorKey, now);
  return true;
};

/**
 * Generate a unique key for an error (for deduplication)
 */
const getErrorKey = (error) => {
  return `${error.name || 'Error'}:${
    error.message?.slice(0, 100) || 'unknown'
  }`;
};

const getMonitoringEndpoint = () => {
  const baseUrl =
    environmentConfig?.api?.baseURL ||
    environmentConfig?.API_BASE_URL ||
    environmentConfig?.apiUrl ||
    '';

  if (!baseUrl) {
    return '/api/admin/monitoring/errors/frontend';
  }

  return `${baseUrl.replace(/\/$/, '')}/api/admin/monitoring/errors/frontend`;
};

/**
 * Error Reporting Service
 */
const errorReportingService = {
  /**
   * Report an error to the backend monitoring system.
   * @param {Error} error - The error object
   * @param {Object} context - Additional context
   * @param {string} context.category - Error category (e.g., 'api', 'render', 'user_action')
   * @param {string} context.component - Component where error occurred
   * @param {Object} context.metadata - Additional metadata
   */
  reportError: async (error, context = {}) => {
    // Skip in development unless explicitly enabled
    if (
      process.env.NODE_ENV === 'development' &&
      !process.env.REACT_APP_ENABLE_ERROR_REPORTING
    ) {
      logger.warn('[ErrorReporting] Skipped in development:', error.message);
      return;
    }

    // Skip if already reported recently (debounce)
    const errorKey = getErrorKey(error);
    if (!shouldReportError(errorKey)) {
      logger.debug('[ErrorReporting] Debounced duplicate error');
      return;
    }

    try {
      const payload = {
        errorType: error.name || 'Error',
        message: error.message || 'Unknown error',
        stackTrace: error.stack,
        source: 'frontend',
        category: context.category || 'frontend_error',
        metadata: {
          url: window.location.href,
          component: context.component,
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString(),
          ...context.metadata,
        },
      };

      await fetch(getMonitoringEndpoint(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'omit',
        mode: 'cors',
        body: JSON.stringify(payload),
      });

      logger.debug('[ErrorReporting] Error reported successfully');
    } catch (reportingError) {
      // Don't throw - we don't want error reporting to cause more errors
      logger.warn('[ErrorReporting] Failed to report error:', reportingError);
    }
  },

  /**
   * Report an API error with request context.
   * @param {Error} error - The error object
   * @param {Object} requestConfig - Axios request config
   */
  reportApiError: async (error, requestConfig = {}) => {
    // Skip 4xx client errors (not really "errors" from monitoring perspective)
    const status = error.response?.status;
    if (status && status >= 400 && status < 500) {
      return;
    }

    await errorReportingService.reportError(error, {
      category: 'api_error',
      metadata: {
        url: requestConfig.url,
        method: requestConfig.method,
        status: error.response?.status,
        responseData: error.response?.data,
      },
    });
  },

  /**
   * Report a render error from ErrorBoundary.
   * @param {Error} error - The error object
   * @param {Object} errorInfo - React error info (componentStack)
   */
  reportRenderError: async (error, errorInfo = {}) => {
    await errorReportingService.reportError(error, {
      category: 'render_error',
      metadata: {
        componentStack: errorInfo.componentStack,
      },
    });
  },

  /**
   * Report a user action error (e.g., form submission failed).
   * @param {Error} error - The error object
   * @param {string} action - The user action that failed
   * @param {Object} metadata - Additional context
   */
  reportUserActionError: async (error, action, metadata = {}) => {
    await errorReportingService.reportError(error, {
      category: 'user_action_error',
      metadata: {
        action,
        ...metadata,
      },
    });
  },

  /**
   * Clear the debounce cache (useful for testing).
   */
  clearDebounceCache: () => {
    errorDebounceMap.clear();
  },
};

export default errorReportingService;
